/** @format */

import { makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography, withStyles } from '@material-ui/core';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateHelper } from '../../../helpers/DateHelper';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import EditButton from '../../../components/Buttons/EditButton';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import I18n from '../../../components/Translate/I18n';
import { pricelistSchemaConstants, pricelistsReducersType } from '../../../modules/pricelists/reducers';
import Card from '../../../components/Card/Card.jsx';
import CardBody from '../../../components/Card/CardBody.jsx';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import PricelistSchemaInterface from '../../../interfaces/billing/PricelistSchemaInterface';
import Pager from '../../../components/Adescom/Pager';
import CloneButton from '../../../components/Buttons/CloneButton';
import { PricelistSchemaHelper } from '../../../helpers/PricelistSchemaHelper';
import Order from '../../../interfaces/forms/Order';
import Filter from '../../../interfaces/forms/Filter';
import DeleteButton from '../../../components/Buttons/DeleteButton';
import TariffInterface, { TariffCollectionInterface } from '../../../interfaces/billing/TariffInterface';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { tariffConstants } from '../../../modules/tariffs/actions';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import AlertConfirm from '../../../components/Info/AlertConfirm';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import ExcelButton from '../../../components/Buttons/ExcelButton';
import { StorageProductToCsv } from '../../../modules/storage/StorageProductToCsv';
import { Auth, tokenInfo } from '../../../modules/auth/Auth';
import { PricelistSchemaToCsv } from './Csv/PricelistSchemaToCsv';

interface PricelistSchemaListProps {
    classes: any;
    editPricelist: (param: { pricelistId: number }) => void;
    loadPricelistSchemaList: (uri?: string) => void;
    clonePricelistHandler: (param: { id: number; type: string }) => void;
    updateSchemaOrderList: (orders: Array<Order>) => void;
    updateSchemaFilterList: (filters: Array<Filter>) => void;
    schemaFilters: Array<Filter>;
    schemaOrders: Array<Order>;
}

const PricelistSchemaList = (props: PricelistSchemaListProps): React.ReactElement => {
    const dispatch = useDispatch();
    const classes = props.classes;

    const TARIFF_URI = '/tariffs';

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    const [alertContent, setAlertContent] = useState<string | React.ReactElement | null>(null);
    const [selectedPricelistSchemaId, setSelectedPricelistSchemaId] = useState<number | null>(null);

    const { collections } = useSelector((state: { pricelistSchemasReducers: pricelistsReducersType }) => {
        return state.pricelistSchemasReducers;
    });

    const paginationCallbackFunction = (uri?: string): void => {
        props.loadPricelistSchemaList(uri);
    };

    const deletePricelistHandler = (param: { id: number }): void => {
        setAlertContent('general:common.loading');
        setSelectedPricelistSchemaId(param.id);
        if (param && param.id) {
            const itemLoaders = new ItemLoaders(dispatch);

            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(TARIFF_URI)),
                tariffConstants.LIST,
                [{ name: 'pricelistSchemaId', value: param.id }],
                [],
                (data: TariffCollectionInterface) => {
                    if (data && data['hydra:member'] && data['hydra:member'].length > 0) {
                        setAlertContent(prepareAlertContent(data['hydra:member']));
                    } else {
                        setAlertContent('pricelists:notify.delete_pricelist');
                    }
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    };

    const prepareAlertContent = (data: Array<TariffInterface>): React.ReactElement => {
        const description = (
            <Typography variant="subtitle2">
                <I18n trans={'pricelists:notify.delete_pricelist_active'} />
            </Typography>
        );

        const content = (
            <div style={{ marginTop: 20 }}>
                {data.map((el: TariffInterface) => {
                    return <div key={el.tariffName}>{el.tariffName}</div>;
                })}
            </div>
        );

        return (
            <>
                {description}
                {content}
            </>
        );
    };

    const confirmDelete = (): void => {
        const itemLoader = new ItemLoaders(dispatch);

        cancelDelete();
        itemLoader.deleteCall(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/pricelist_schemas/' + selectedPricelistSchemaId)),
            pricelistSchemaConstants.DELETE,
            (): void => {
                paginationCallbackFunction();
                NotificationHelper.notifySuccess('pricelists:notify.pricelist_schema_delete_success', dispatch);
            },
            {
                dispatchAfterCallback: true,
                dispatchErrorAfterCallback: true,
            }
        );
    };

    const cancelDelete = (): void => {
        setAlertContent(null);
    };

    const exportToCsv = (params: { pricelistSchemaId: number; pricelistSchemaName: string }): void => {
        const itemLoader = new ItemLoaders(dispatch);
        const toCsv = new PricelistSchemaToCsv(itemLoader);
        toCsv.exportToCsv(params);
    };

    return (
        <>
            <Card>
                <CardHeader icon={<AccountTreeIcon />} trans="pricelists:h.pricelists_groups" />
                <CardBody>
                    <GridContainer>
                        <GridItem xs={12} md={12} lg={12} style={{ marginBottom: 20 }}>
                            <Table classes={props.classes.tableRootNotMinWidth}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell classes={tableStickyCommand} padding="none" align="left">
                                            <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="left">
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="pricelists:h.name"
                                                updateOrderList={props.updateSchemaOrderList}
                                                updateFilterList={props.updateSchemaFilterList}
                                                fieldName="mainname"
                                                showSort={true}
                                                defaultDirection="desc"
                                                searchFieldType="string"
                                                filters={props.schemaFilters}
                                                sort={props.schemaOrders}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="left">
                                            <TableHeaderContentCell textAlign="left" title="pricelists:h.activated_at" />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {collections &&
                                        collections['hydra:member'].map((el: PricelistSchemaInterface) => {
                                            return (
                                                <TableRow key={el.id}>
                                                    <TableCell classes={tableStickyHeader} align="left">
                                                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_TARIFF_PUT) && (
                                                            <EditButton
                                                                tooltipTitle="pricelists:b.edit_pricelist"
                                                                callback={props.editPricelist}
                                                                callbackParam={{ pricelistId: el.id }}
                                                            />
                                                        )}
                                                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_TARIFF_POST) && (
                                                            <CloneButton
                                                                tooltipTitle="pricelists:b.clone"
                                                                callback={props.clonePricelistHandler}
                                                                callbackParam={{
                                                                    id: el.id,
                                                                    type: PricelistSchemaHelper.PRICELIST_CLONE_TYPE.PRICELIST_SCHEMA,
                                                                }}
                                                            />
                                                        )}
                                                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PRICELIST_SCHEMA_DELETE) && (
                                                            <DeleteButton
                                                                tooltipTitle="fractions:b.delete"
                                                                callback={deletePricelistHandler}
                                                                callbackParam={{ id: el.id }}
                                                            />
                                                        )}
                                                        {
                                                            <ExcelButton
                                                                tooltipTitle="fractions:b.export_to_csv"
                                                                callback={exportToCsv}
                                                                callbackParam={{ pricelistSchemaId: el.id, pricelistSchemaName: el.mainname }}
                                                            />
                                                        }
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {el.mainname}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {DateHelper.formatDDMMYYYY(el.activatedAt)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell classes={tableCellRoot} colSpan={4}>
                                            {collections && collections['hydra:view'] && (
                                                <Pager callback={paginationCallbackFunction} views={collections['hydra:view']} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </GridItem>
                    </GridContainer>
                </CardBody>
            </Card>

            {alertContent && (
                <AlertConfirm
                    onConfirm={confirmDelete}
                    onCancel={cancelDelete}
                    confirmBtnText={'general:common.confirm'}
                    cancelBtnText={'general:common.cancel'}
                    content={alertContent}
                />
            )}
        </>
    );
};

export default withStyles(oplAppStyle)(PricelistSchemaList);
